import Link from 'next/link';

import { ReactNode } from 'react';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { DeserializedSelfPool, LaunchpadSalePoolType } from '@api';

import { formatNumberAsCurrency, formatNumberLocalized } from '@shared/helpers/formatters';

import { DepositFeeTooltip } from '@launchpad/components/ui/depositFee/DepositFeeTooltip';

export interface PortfolioBasePoolStat {
  id: string;
  label?: ReactNode;
  content?: ReactNode;
  className?: string;
}

// TODO: refactor
export const getNumberWithSymbol = (price: number, tokenSymbol = 'USDT', isSymbolLast = false) => {
  if (tokenSymbol.toLowerCase() === 'usdt') {
    return formatNumberAsCurrency({ amount: price, maximumFractionDigits: 4 });
  }

  const formattedPrice = formatNumberLocalized(price, { options: { maximumFractionDigits: 4 } });

  return isSymbolLast ? `${formattedPrice} ${tokenSymbol}` : `${tokenSymbol} ${formattedPrice}`;
};

export const getTokenPriceStat = (price: number, tokenSymbol: string): PortfolioBasePoolStat => {
  return {
    id: 'token-price',
    label: 'Token Price',
    content: getNumberWithSymbol(price, tokenSymbol, true),
  };
};

export const getDepositedAmountStat = ({
  fee = 0,
  depositedAmount = 0,
  refundedAmount,
  totalDeposited = 0,
  tokenSymbol = 'USDT',
  refundToken = 'USDT',
  poolType,
  isRefunded,
}: {
  fee?: number;
  depositedAmount?: number | null;
  refundedAmount?: number | null;
  totalDeposited: number | undefined;
  tokenSymbol: string | undefined;
  poolType: LaunchpadSalePoolType;
  refundToken: string | undefined;
  isRefunded?: boolean;
}): PortfolioBasePoolStat => {
  const showCalculationInProgress =
    typeof refundedAmount !== 'number' || typeof depositedAmount !== 'number';

  if (isRefunded) {
    return {
      id: 'deposit',
      label: 'My Allocation',
      content: getNumberWithSymbol(0, tokenSymbol, true),
    };
  }

  return {
    id: 'deposit',
    label: (
      <>
        My Allocation
        {
          <DepositFeeTooltip
            amount={totalDeposited}
            feeCalculatedValue={fee}
            currency={tokenSymbol}
            poolType={poolType}
          >
            <div>
              <div>
                Amount to Refund:{' '}
                {showCalculationInProgress
                  ? 'Calculating...'
                  : getNumberWithSymbol(refundedAmount, refundToken, true)}
              </div>
              <div>
                My Allocation After Refund:{' '}
                {showCalculationInProgress
                  ? 'Calculating...'
                  : getNumberWithSymbol(depositedAmount, tokenSymbol, true)}
              </div>
            </div>
          </DepositFeeTooltip>
        }
      </>
    ),
    content: showCalculationInProgress ? (
      <span className="text-sm">Calculating...</span>
    ) : (
      <>
        {getNumberWithSymbol(depositedAmount || 0, tokenSymbol, true)}
        <DepositFeeTooltip
          amount={depositedAmount || 0}
          feeCalculatedValue={fee}
          currency={tokenSymbol}
          poolType={poolType}
        />
      </>
    ),
  };
};

interface PortfolioBasePoolProps {
  pool: DeserializedSelfPool;
  stats: PortfolioBasePoolStat[];
  action: ReactNode;
  footer?: ReactNode;
  variant?: 'success';
}

// const POOL_TYPE_NAME = {
//   [LAUNCHPAD_SALE_POOL_TYPE.FCFS]: 'FCFS',
//   [LAUNCHPAD_SALE_POOL_TYPE.SQRP_GATED]: 'SQRp Pool',
//   [LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA]: 'Pro-Rata',
//   [LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA_SQRP_GATED]: 'Pro-Rata SQRp Pool',
// };

// const getPoolTypeName = (poolType: LaunchpadSalePoolType | (string & NonNullable<unknown>)) => {
//   return POOL_TYPE_NAME[poolType as LaunchpadSalePoolType] || 'Unknown Type';
// };

const PortfolioBasePool = ({ stats, action, footer, pool, variant }: PortfolioBasePoolProps) => {
  const isSuccessVariant = variant === 'success';

  const project = pool.project || {
    name: 'N/A',
    logoUrl: '',
    ticker: 'N/A',
    slug: '#',
  };

  return (
    <div
      className={classNames(
        'p-3 lg:p-4 flex flex-col justify-center gap-4 min-h-[4.75rem] rounded-lg',
        {
          'bg-green-50': isSuccessVariant,
          'bg-primary-50': !isSuccessVariant,
        },
      )}
      data-qa={pool.id}
    >
      <div
        className={classNames(
          'flex lg:items-center max-lg:flex-col gap-2 lg:gap-5 lg:flex-wrap xl:flex-nowrap',
        )}
      >
        <Link
          href={`/projects/${project.slug}`}
          className="flex justify-start gap-2 items-start lg:basis-64 flex-shrink overflow-hidden"
        >
          <ImageProxy
            alt={`${project.name}'s icon`}
            src={project.logoUrl}
            width={42}
            height={42}
            className="rounded-full size-[2.625rem] flex-shrink-0"
          />

          <div className="overflow-hidden">
            <div className="flex items-baseline gap-1">
              <h5 className="text-[1.0625rem] leading-[1.4117] font-semibold truncate">
                {project.name}
              </h5>

              <span className="text-[0.6875rem] text-neutral-500 uppercase">{project.ticker}</span>
            </div>

            <p className="text-[0.8125rem] truncate leading-[1.3846] lg:text-[0.8125rem] lg:leading-[1.125] text-neutral-500">
              Pool: {pool.name}
            </p>
          </div>
        </Link>

        <ul
          className={classNames(
            'flex flex-col flex-1 lg:items-center gap-3 lg:flex-row lg:basis-[480px] lg:whitespace-nowrap',
          )}
        >
          {stats.map(({ id, label, content, className }) => {
            return (
              <li
                className={classNames(
                  'flex flex-1 max-lg:gap-5 max-lg:items-baseline max-lg:justify-between lg:flex-col-reverse',
                  className,
                )}
                key={id}
              >
                {Boolean(label) && (
                  <span className="text-sm truncate leading-[1.3846] lg:text-[0.8125rem] lg:leading-[1.125] text-neutral-500">
                    {label}
                  </span>
                )}
                {Boolean(content) && (
                  <span
                    className={classNames('font-bold truncate leading-[1.4117] text-[1.0625rem]', {
                      'max-lg:text-right': Boolean(label),
                    })}
                  >
                    {content}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {Boolean(footer) && <div className="lg:hidden flex flex-col gap-2 mt-3">{footer}</div>}

        <div
          className={classNames(
            'sm:min-w-[21rem] flex items-center gap-2 text-center justify-end lg:justify-start xl:justify-end max-lg:mt-1 max-lg:[&>*]:w-full ',
            // NOTE: preserve empty space for alignment on large devices in case there's no action
            'max-lg:empty:hidden',
          )}
        >
          {action}
        </div>
      </div>

      {Boolean(footer) && <div className="max-lg:hidden flex flex-col gap-4">{footer}</div>}
    </div>
  );
};

export default PortfolioBasePool;
